import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Document from '../../../../../shared/shim/react-pdf/Document';
import Page from '../../../../../shared/shim/react-pdf/Page';
import FooterWrapper, { inferFooterWrapperFixPacket } from '../FooterWrapper';
import DocumentFooter, {
    inferDocumentModalFooter,
} from './DocumentModalFooter';
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types';
import { inferRedact } from 'shared/shim/react-pdf/PageCanvas';
import { useClickAway, useUpdateEffect } from 'react-use';
import isPointerInside from 'shared/helpers/isPointerInside';
import { ManagePacketService } from 'services/ManagePacketService';
import { useFormikContext } from 'formik';
import { FormikPropsFixPacketValues } from '../NotExpectedPage';
import { Utils } from 'shared/helpers';
import { CircularProgress, Typography } from '@mui/material';
import { inferDocumentReworkModal } from './ReworkModal';
import { DocumentPDF } from '../../solicitor-viewer/components/DocumentViewer';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { AxiosError } from 'axios';

const DocumentModal: React.FC<Props> = React.forwardRef(
    (
        {
            callbackClosingModal,
            callbackOpenModal,
            categoryMenuName,
            categoryData,
            categoryIndex,
            categoryDefaultValue,
            document,
            clientId,
            packetId,
            binNumber,
            canCategorize,
            showCategory,
        },
        ref
    ): React.ReactElement => {
        const containerModalRef = React.useRef<HTMLDivElement | null>(null);
        const { validateForm, setFieldTouched } =
            useFormikContext<FormikPropsFixPacketValues>();
        const [numPages, setNumPages] = React.useState<number | null>(null);
        const [pdfUrl, setPdfUrl] = React.useState<
            string | null | { url: string }
        >(null);
        const [pdfInitialization, setPdfInitialization] =
            React.useState<boolean>(false);
        const [isDocumentSuccess, setLoadingDocumentSuccess] =
            React.useState<boolean>(false);
        const redactRef = React.useRef<Array<inferRedact> | null>(null);
        const canvasRef = React.useRef<Array<HTMLCanvasElement>>([]);
        const documentRef = React.useRef<DocumentPDF | null>(null);
        const footerWrapperRef =
            React.useRef<inferFooterWrapperFixPacket | null>(null);
        const footerRef = React.useRef<inferDocumentModalFooter | null>(null);
        const modalDocumentReworkRef =
            React.useRef<inferDocumentReworkModal | null>(null);
        const handle = useFullScreenHandle();
        const [isFullScreen, setFullScreen] = React.useState<boolean>(false);
        // const [historyRedacts, setRedactsHistory] = React.useState<populatedRedacts>({});

        useUpdateEffect(() => {
            if (isFullScreen) {
                handle.enter();
            } else {
                handle.exit();
            }
        }, [isFullScreen]);

        const [isPdfNetworkError, setPdfNetworkError] =
            React.useState<boolean>(false);
        // const [historyRedacts, setRedactsHistory] = React.useState<populatedRedacts>({});

        /**
         * handle action when document load is successful
         */
        const onDocumentLoadSuccess = (props: DocumentCallback): void => {
            setNumPages(props.numPages);
            setLoadingDocumentSuccess(true);
            setPdfNetworkError(false);
        };

        const [open, setOpen] = React.useState(false);

        React.useLayoutEffect(() => {
            if (!open) {
                if (isDocumentSuccess) {
                    setLoadingDocumentSuccess(false);
                    setPdfUrl(null);
                    setPdfInitialization(false);
                    setPdfNetworkError(false);
                } else {
                    setPdfUrl({ url: '' });
                    setPdfNetworkError(false);
                }
            }
        }, [open]);
        React.useEffect(() => {
            if (open && document) {
                ManagePacketService.getSasToken({
                    fileName: document?.file,
                    report: false,
                })
                    .then((response) => {
                        setPdfInitialization(true);
                        setPdfUrl(response.data.data.url);
                    })
                    .catch((error) => {
                        setPdfInitialization(true);
                        setPdfUrl(null);
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            setPdfNetworkError(true);
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            }
        }, [document, open]);
        React.useLayoutEffect(() => {
            if (open && isDocumentSuccess) {
                const { current: footer } = footerRef;
                if (open && document) {
                    footer?.setDefaultDocumentData(
                        document,
                        clientId,
                        packetId,
                        binNumber,
                        categoryData
                    );
                }
            }
        }, [open, isDocumentSuccess]);
        /**
         * prepare document modal when trigger callback from footer to open modal
         */
        const handleModalChangedInsideFooter = React.useCallback(() => {
            const { current: footer } = footerRef;
            if (open && document) {
                footer?.setDefaultDocumentData(
                    document,
                    clientId,
                    packetId,
                    binNumber,
                    categoryData
                );
            }
        }, [open, document, clientId, packetId, binNumber, categoryData]);
        React.useState<boolean>(false);

        const outsideContainerModalRef = React.useRef<HTMLDivElement>(null);

        /**
         * handle clickaway listener for closing modal in backdrop selection
         * @param {MouseEvent} parameteeventrNameHere - event
         */
        const handleClickAwayListener = React.useCallback(
            (event: MouseEvent) => {
                const { current: footerWrapper } = footerWrapperRef;
                const { current: footer } = footerRef;
                const { current: container } = containerModalRef;
                if (
                    container &&
                    footerWrapper?.isPointerOnFooter(event) === false &&
                    isPointerInside(container, event) &&
                    footer?.isModalFooterActive() === false
                ) {
                    setOpen(false);
                }
            },
            []
        );
        useClickAway(outsideContainerModalRef, handleClickAwayListener);

        /**
         * handle modal open
         */
        const handleOpen = React.useCallback((): void => {
            setOpen(true);
            if (typeof callbackOpenModal === 'function') callbackOpenModal();
        }, [callbackOpenModal]);
        /**
         * handle modal close
         */
        const handleClose = React.useCallback((): void => {
            const { current: footer } = footerRef;
            if (footer?.isModalFooterActive() === false) {
                setOpen(false);
                if (typeof callbackClosingModal === 'function')
                    callbackClosingModal(null);
            }
        }, [callbackClosingModal]);

        /**
         * handle save process from footer
         */
        const handleSave = React.useCallback(async () => {
            const validate = await validateForm()
                .then((response) => {
                    if (
                        response &&
                        Object.keys(response).includes('documents')
                    ) {
                        if (
                            Array.isArray(response.documents) &&
                            response.documents.length > 0 &&
                            response.documents.some(
                                (o, i) =>
                                    i === categoryIndex && typeof o === 'string'
                            )
                        ) {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].category`,
                                    true
                                );
                            });
                            return false;
                        }
                        return true;
                    }

                    return true;
                })
                .catch(() => false);
            if (
                document &&
                (validate || showCategory === false || canCategorize === false)
            ) {
                setOpen(false);
                if (typeof callbackClosingModal === 'function')
                    callbackClosingModal(null);
                // ManagePacketService.updateCategory({
                //     clientId,
                //     packetId,
                //     documents: values.documents.filter(
                //
                //         (o) => o._id === document._id
                //     ),
                // })
                //     .then((response) => {
                //         setOpen(false);
                //         if (typeof callbackClosingModal === 'function')
                //             callbackClosingModal(null);
                //         Utils.handleSuccessResponse(response);
                //     })
                //     .catch((error) => {
                //         Utils.handleErrorResponse(error);
                //     });
            }
        }, [
            callbackClosingModal,
            canCategorize,
            categoryIndex,
            document,
            setFieldTouched,
            showCategory,
            validateForm,
        ]);
        const [width, setWidth] = React.useState<number>(400 + 25 * 20);
        const [height, setHeight] = React.useState<number>(400 + 25 * 20);
        const handleZoom = React.useCallback((value: number) => {
            setWidth(400 + 25 * value);
            setHeight(400 + 25 * value);
        }, []);
        React.useImperativeHandle(
            ref,
            () => ({
                setClose: () => {
                    handleClose();
                },
                setOpen: () => {
                    handleOpen();
                },
            }),
            [handleOpen, handleClose]
        );
        return (
            <Box className="DocumentModal">
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description"
                    disableScrollLock
                    slotProps={{
                        backdrop: {
                            sx: {
                                backgroundColor: `rgba(0, 0, 0, ${
                                    isPdfNetworkError ? 0.8 : 0.5
                                })`, // Adjust the last value (0.7) for desired opacity
                            },
                        },
                    }}
                >
                    <Box className="container-not-expected-footer-content">
                        <FullScreen
                            handle={handle}
                            className="handle-fullscreen-viewer"
                            onChange={(state) => {
                                if (!state) setFullScreen(false);
                            }}
                        >
                            <FooterWrapper
                                innerModalDocumentReworkRef={
                                    modalDocumentReworkRef
                                }
                                innerRef={footerRef}
                                ref={footerWrapperRef}
                                Footer={DocumentFooter}
                                documentPlaceholder={
                                    document?.placeholder ?? ''
                                }
                                isDocumentSuccess={isDocumentSuccess}
                                onModalChanged={handleModalChangedInsideFooter}
                                onClose={handleSave}
                                onBack={handleClose}
                                onZoomChanged={handleZoom}
                                categoryMenuName={categoryMenuName}
                                categoryDefaultValue={categoryDefaultValue}
                                documentRef={documentRef}
                                canCategorize={canCategorize}
                                isFullScreen={isFullScreen}
                                setFullScreen={setFullScreen}
                                unexpectedType={false}
                                allowRerun={false}
                                updateGroup={false}
                                allowRevalidation={false}
                            >
                                <Box
                                    className={
                                        isPdfNetworkError
                                            ? 'container-document-modal modal-network-error'
                                            : 'container-document-modal'
                                    }
                                    ref={containerModalRef}
                                >
                                    <Box
                                        className="inner-container-document"
                                        ref={outsideContainerModalRef}
                                    >
                                        <Document
                                            className="document-viewer"
                                            ref={documentRef}
                                            renderMode="canvas"
                                            file={pdfUrl}
                                            error={
                                                <Box
                                                    className={
                                                        isPdfNetworkError
                                                            ? 'align-loader network-error'
                                                            : 'align-loader'
                                                    }
                                                >
                                                    <Typography variant="h3">
                                                        {isPdfNetworkError
                                                            ? 'No internet connection, Please try again!'
                                                            : 'Document Missing'}
                                                    </Typography>
                                                </Box>
                                            }
                                            noData={
                                                <Box
                                                    className={
                                                        isPdfNetworkError
                                                            ? 'align-loader network-error'
                                                            : 'align-loader'
                                                    }
                                                >
                                                    {isDocumentSuccess ===
                                                        false &&
                                                    (pdfUrl ||
                                                        pdfInitialization ===
                                                            false) ? (
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    ) : (
                                                        <Typography variant="h3">
                                                            {isPdfNetworkError
                                                                ? 'No internet connection, Please try again!'
                                                                : 'Document Missing'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                            loading={
                                                <Box className="align-loader">
                                                    <CircularProgress
                                                        className="mr-2 margin-loader"
                                                        size={54}
                                                        color="inherit"
                                                        title="progress loading"
                                                    />
                                                </Box>
                                            }
                                            onLoadSuccess={
                                                onDocumentLoadSuccess
                                            }
                                            onLoadNetworkError={() => {
                                                setPdfNetworkError(true);
                                            }}
                                        >
                                            {Array.from({
                                                length: numPages ?? 0,
                                            }).map((el, index) => (
                                                <Page
                                                    className="page-document-viewer"
                                                    key={index}
                                                    pageNumber={index + 1}
                                                    loading={
                                                        <Box className="align-loader">
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        </Box>
                                                    }
                                                    error={
                                                        <Box className="align-loader">
                                                            <Typography variant="h3">
                                                                Document Page
                                                                Missing
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    redactRef={(_ref) => {
                                                        if (
                                                            _ref !== null &&
                                                            redactRef.current !==
                                                                null
                                                        ) {
                                                            redactRef.current[
                                                                index
                                                            ] = _ref;
                                                        }
                                                    }}
                                                    canvasRef={(_ref) => {
                                                        if (_ref !== null) {
                                                            canvasRef.current[
                                                                index
                                                            ] = _ref;
                                                        }
                                                    }}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={
                                                        false
                                                    }
                                                    height={height}
                                                    width={width}
                                                />
                                            ))}
                                        </Document>
                                    </Box>
                                </Box>
                            </FooterWrapper>
                        </FullScreen>
                    </Box>
                </Modal>
            </Box>
        );
    }
);

export default DocumentModal;
